/* import __COLOCATED_TEMPLATE__ from './auto-snooze-on-away-section.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */

import type IntlService from 'embercom/services/intl';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { ref } from 'ember-ref-bucket';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';

interface Args {
  autoSnoozeEnabled: boolean;
  snoozeDuration: number;
  snoozeUnit: string;
  updateTrackedProperty: (key: string, value: boolean | number | string) => void;
  persistAutoSnoozeOnAwaySetting?: () => void;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class AutoSnoozeOnAwaySection extends Component<Signature> {
  @service declare intl: IntlService;

  @ref('snooze-duration-input') snoozeDurationInput?: HTMLInputElement;

  MIN_DURATION = 1;
  MAX_DURATION = 999;

  get unitOptions() {
    return [
      {
        value: 'minutes',
        label: this.intl.t(
          'new-settings.helpdesk.assignments.other.auto-snooze-on-away-section.units.minutes',
        ),
      },
      {
        value: 'hours',
        label: this.intl.t(
          'new-settings.helpdesk.assignments.other.auto-snooze-on-away-section.units.hours',
        ),
      },
      {
        value: 'days',
        label: this.intl.t(
          'new-settings.helpdesk.assignments.other.auto-snooze-on-away-section.units.days',
        ),
      },
      {
        value: 'weeks',
        label: this.intl.t(
          'new-settings.helpdesk.assignments.other.auto-snooze-on-away-section.units.weeks',
        ),
      },
    ];
  }

  @action
  toggleAutoSnooze() {
    this.args.updateTrackedProperty('autoSnoozeEnabled', !this.args.autoSnoozeEnabled);
    if (this.args.persistAutoSnoozeOnAwaySetting) {
      this.args.persistAutoSnoozeOnAwaySetting();
    }
    // Focus input when enabling (check negated value since we just toggled)
    if (!this.args.autoSnoozeEnabled && this.snoozeDurationInput) {
      this.snoozeDurationInput.disabled = false;
      this.snoozeDurationInput.focus();
    }
  }

  @action
  setSnoozeDuration(event: Event) {
    let target = event.target as HTMLInputElement;
    let value = parseInt(target.value, 10);

    this.args.updateTrackedProperty('snoozeDuration', value);
    if (this.args.persistAutoSnoozeOnAwaySetting) {
      debounce(this, this.args.persistAutoSnoozeOnAwaySetting, 500);
    }
  }

  @action
  setSnoozeUnit(event: Event) {
    let target = event.target as HTMLSelectElement;
    let value = target.value;

    // Validate unit is one of the allowed values
    let validUnits = ['minutes', 'hours', 'days', 'weeks'];
    if (!validUnits.includes(value)) {
      return; // Ignore invalid values
    }

    this.args.updateTrackedProperty('snoozeUnit', value);
    if (this.args.persistAutoSnoozeOnAwaySetting) {
      this.args.persistAutoSnoozeOnAwaySetting();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Helpdesk::TabsComponents::AutoSnoozeOnAwaySection': typeof AutoSnoozeOnAwaySection;
  }
}
